import { client } from '@/api/common';
import {
  API,
  BASE,
  URL_COMPANIES,
  URL_TENANTS,
  V1,
} from '@/lib/constants/urls.ts';

import type { Company } from '@repo/database/browser';
import type {
  CommercialHistoryListDto,
  CommercialPriceRowDto,
  CommercialProductItemDto,
  CommercialSettlementListDto,
  CompanyCountriesResponseDto,
  CounterpartMaterialItemDto,
  CounterpartMaterialListDto,
  CreateCounterpartMaterialDto,
  CreateCounterpartMaterialReturnDto,
  FetchCounterpartMaterialsDto,
  CompanyFilterDto,
  CompanyStatsDto,
  FetchCommercialHistoryDto,
  FetchCommercialPricesDto,
  FetchCommercialProductsDto,
  FetchCommercialSettlementsDto,
  CreateCompanyDto,
  ListResponse,
  UpdateCompanyDto,
} from '@repo/api';
import type { AddNullToUndefined } from '@repo/utils';

export const fetchCompany = async (
  tenantId: string,
  id?: string
): Promise<Company | undefined> => {
  const response = await client<Company>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}/${id}`,
    method: 'GET',
  });

  return response.data;
};

export const fetchCompanyStats = async (
  tenantId: string,
  id: string
): Promise<CompanyStatsDto> => {
  const response = await client<CompanyStatsDto>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}/${id}/stats`,
    method: 'GET',
  });

  return response.data;
};

const companyUrl = (tenantId: string, id: string) =>
  `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}/${id}`;

/** Endpoint dell'export dello storico, nel formato atteso da `useExport`. */
export const companyCommercialHistoryExportEndpoint = (
  tenantId: string,
  id: string
) => `${URL_TENANTS}/${tenantId}${URL_COMPANIES}/${id}/commercial-history`;

export const fetchCompanyCommercialHistory = async (
  tenantId: string,
  id: string,
  params: FetchCommercialHistoryDto
): Promise<CommercialHistoryListDto> => {
  const response = await client<CommercialHistoryListDto>({
    url: `${companyUrl(tenantId, id)}/commercial-history`,
    method: 'GET',
    params,
  });

  return response.data;
};

export const fetchCompanyCommercialProducts = async (
  tenantId: string,
  id: string,
  params: FetchCommercialProductsDto
): Promise<ListResponse<CommercialProductItemDto>> => {
  const response = await client<ListResponse<CommercialProductItemDto>>({
    url: `${companyUrl(tenantId, id)}/commercial-products`,
    method: 'GET',
    params,
  });

  return response.data;
};

export const fetchCompanyCommercialPrices = async (
  tenantId: string,
  id: string,
  params: FetchCommercialPricesDto
): Promise<ListResponse<CommercialPriceRowDto>> => {
  const response = await client<ListResponse<CommercialPriceRowDto>>({
    url: `${companyUrl(tenantId, id)}/commercial-prices`,
    method: 'GET',
    params,
  });

  return response.data;
};

export const fetchCompanyCommercialSettlements = async (
  tenantId: string,
  id: string,
  params: FetchCommercialSettlementsDto
): Promise<CommercialSettlementListDto> => {
  const response = await client<CommercialSettlementListDto>({
    url: `${companyUrl(tenantId, id)}/commercial-settlements`,
    method: 'GET',
    params,
  });

  return response.data;
};

export const fetchCompanyMaterials = async (
  tenantId: string,
  id: string,
  params: FetchCounterpartMaterialsDto
): Promise<CounterpartMaterialListDto> => {
  const response = await client<CounterpartMaterialListDto>({
    url: `${companyUrl(tenantId, id)}/commercial-materials`,
    method: 'GET',
    params,
  });

  return response.data;
};

export const createCompanyMaterial = async (
  tenantId: string,
  id: string,
  data: CreateCounterpartMaterialDto
): Promise<CounterpartMaterialItemDto> => {
  const response = await client<CounterpartMaterialItemDto>({
    url: `${companyUrl(tenantId, id)}/commercial-materials`,
    method: 'POST',
    data,
  });

  return response.data;
};

export const deleteCompanyMaterial = async (
  tenantId: string,
  id: string,
  materialId: string
): Promise<void> => {
  await client({
    url: `${companyUrl(tenantId, id)}/commercial-materials/${materialId}`,
    method: 'DELETE',
  });
};

export const createCompanyMaterialReturn = async (
  tenantId: string,
  id: string,
  materialId: string,
  data: CreateCounterpartMaterialReturnDto
): Promise<CounterpartMaterialItemDto> => {
  const response = await client<CounterpartMaterialItemDto>({
    url: `${companyUrl(tenantId, id)}/commercial-materials/${materialId}/returns`,
    method: 'POST',
    data,
  });

  return response.data;
};

export const deleteCompanyMaterialReturn = async (
  tenantId: string,
  id: string,
  materialId: string,
  returnId: string
): Promise<CounterpartMaterialItemDto> => {
  const response = await client<CounterpartMaterialItemDto>({
    url: `${companyUrl(tenantId, id)}/commercial-materials/${materialId}/returns/${returnId}`,
    method: 'DELETE',
  });

  return response.data;
};

export const fetchCompanyCountries = async (
  tenantId: string,
  isEmployee: boolean
): Promise<CompanyCountriesResponseDto> => {
  const response = await client<CompanyCountriesResponseDto>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}/countries`,
    method: 'GET',
    params: { isEmployee },
  });

  return response.data;
};

export const fetchCompanies = async (
  tenantId: string,
  params?: CompanyFilterDto
): Promise<ListResponse<Company>> => {
  const response = await client<ListResponse<Company>>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}`,
    method: 'GET',
    params,
  });
  return response.data;
};

export const createCompany = async (
  tenantId: string,
  params: AddNullToUndefined<CreateCompanyDto>
): Promise<Company> => {
  const response = await client<Company>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}`,
    method: 'POST',
    data: params,
  });
  return response.data;
};

export const updateCompany = async (
  tenantId: string,
  id: string,
  params: AddNullToUndefined<UpdateCompanyDto>
): Promise<Company> => {
  const response = await client<Company>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}/${id}`,
    method: 'PATCH',
    data: params,
  });
  return response.data;
};

export const deleteCompany = async (tenantId: string, id: string) => {
  const response = await client<Company>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}/${id}`,
    method: 'DELETE',
  });
  return response.data;
};

export const fetchUncategorizedSupplierInvoiceCount = async (
  tenantId: string,
  id: string
): Promise<{ count: number }> => {
  const response = await client<{ count: number }>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_COMPANIES}/${id}/uncategorized-supplier-invoices`,
    method: 'GET',
  });
  return response.data;
};
